"use client";

import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "next/navigation";

interface CommandItem {
  id: string;
  name: string;
  category: string;
  icon: React.ReactNode;
  shortcut?: string;
  action: () => void;
}

// Konami Code sequence: Up, Up, Down, Down, Left, Right, Left, Right, B, A
const konamiCode = ["ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown", "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight", "b", "a"];

function MatrixCanvas({ onClose }: { onClose: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const cols = Math.floor(width / 20) + 1;
    const ypos = Array(cols).fill(0);

    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, width, height);

    const draw = () => {
      ctx.fillStyle = "rgba(2, 1, 8, 0.06)"; // dark purple tint to match theme
      ctx.fillRect(0, 0, width, height);

      ctx.fillStyle = "#a78bfa"; // purple matrix drops
      ctx.font = "11pt monospace";

      ypos.forEach((y, ind) => {
        const chars = "0123456789ABCDEF<>[]{}/\\*&^%$#@!";
        const char = chars[Math.floor(Math.random() * chars.length)];
        const x = ind * 20;
        
        ctx.fillStyle = Math.random() > 0.98 ? "#ffffff" : "#818cf8";
        ctx.fillText(char, x, y);
        
        if (y > 100 + Math.random() * 10000) {
          ypos[ind] = 0;
        } else {
          ypos[ind] = y + 18;
        }
      });
    };

    const interval = setInterval(draw, 33);

    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[95] bg-[#020108]" onClick={onClose}>
      <canvas ref={canvasRef} className="w-full h-full block cursor-pointer" />
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full bg-black/60 border border-white/10 text-white font-mono text-[10px] tracking-widest uppercase select-none pointer-events-none animate-pulse">
        Matrix diagnostic online. Click anywhere to return.
      </div>
    </div>
  );
}

export default function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMatrixActive, setIsMatrixActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const konamiIndexRef = useRef(0);

  useEffect(() => {
    const handleGlobalKeys = (e: KeyboardEvent) => {
      // Konami checker
      if (e.key.toLowerCase() === konamiCode[konamiIndexRef.current].toLowerCase()) {
        konamiIndexRef.current++;
        if (konamiIndexRef.current === konamiCode.length) {
          setIsMatrixActive((prev) => !prev);
          konamiIndexRef.current = 0;
          console.log("✔ Diagnostic Easter Egg: Matrix theme activated.");
        }
      } else {
        konamiIndexRef.current = 0;
      }

      // CP hotkey checker
      const activeEl = document.activeElement;
      const isInput = activeEl && (activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA" || activeEl.getAttribute("contenteditable") === "true");
      
      if (e.key === "/" && !isOpen && !isInput && !isMatrixActive) {
        e.preventDefault();
        setIsOpen(true);
        setSearch("");
        setActiveIndex(0);
      } else if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener("keydown", handleGlobalKeys);
    return () => window.removeEventListener("keydown", handleGlobalKeys);
  }, [isOpen, isMatrixActive]);

  // Console greetings greeting message
  useEffect(() => {
    console.log(
      "%cAMRIT SHARMA | SOFTWARE ARCHITECT %c\nGreetings developer inspecting the DOM! Read the source code clean components patterns in components/ directory.",
      "color: #818cf8; font-weight: bold; font-size: 16px;",
      "color: gray; font-size: 11px;"
    );
  }, []);

  // Handle focus when command palette opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [isOpen]);

  // Listen to custom open command event
  useEffect(() => {
    const handleToggleCP = () => {
      setIsOpen(true);
      setSearch("");
      setActiveIndex(0);
    };
    window.addEventListener("open-command-palette", handleToggleCP);
    
    const handleToggleMatrix = () => {
      setIsMatrixActive(prev => !prev);
    };
    window.addEventListener("toggle-matrix", handleToggleMatrix);

    return () => {
      window.removeEventListener("open-command-palette", handleToggleCP);
      window.removeEventListener("toggle-matrix", handleToggleMatrix);
    };
  }, []);

  const router = useRouter();

  const navigateTo = (path: string) => {
    setIsOpen(false);
    router.push(path);
  };

  const commands: CommandItem[] = [
    {
      id: "about",
      name: "About & Storytelling Biography",
      category: "Information",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      shortcut: "G A",
      action: () => navigateTo("/about"),
    },
    {
      id: "skills",
      name: "Skills & Categorized Tech Stack",
      category: "Skills",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      shortcut: "G S",
      action: () => navigateTo("/"),
    },
    {
      id: "architecture",
      name: "System Architecture & Diagrams",
      category: "Engineering",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      shortcut: "G R",
      action: () => navigateTo("/architecture"),
    },
    {
      id: "projects",
      name: "Featured Digital Masterpieces",
      category: "Projects",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      shortcut: "G P",
      action: () => navigateTo("/projects"),
    },
    {
      id: "playground",
      name: "Developer Playground (Lab Tools)",
      category: "Utility",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      shortcut: "G L",
      action: () => navigateTo("/playground"),
    },
    {
      id: "github",
      name: "GitHub API Metrics & Calendar",
      category: "Social",
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.577.688.479C19.138 20.162 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
        </svg>
      ),
      shortcut: "G G",
      action: () => navigateTo("/open-source"),
    },
    {
      id: "blog",
      name: "Technical Writing & Posts",
      category: "Writing",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      shortcut: "G B",
      action: () => navigateTo("/blog"),
    },
    {
      id: "testimonials",
      name: "Recommendations & Trust Reviews",
      category: "Social",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
      ),
      shortcut: "G T",
      action: () => {
        setIsOpen(false);
        const element = document.getElementById("testimonials");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          router.push("/#testimonials");
        }
      },
    },
    {
      id: "contact",
      name: "Contact Cards & Availability",
      category: "Action",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      shortcut: "G C",
      action: () => {
        setIsOpen(false);
        const element = document.getElementById("contact");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          router.push("/#contact");
        }
      },
    },
    {
      id: "resume",
      name: "View Dossier / PDF Resume",
      category: "Action",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      shortcut: "D R",
      action: () => navigateTo("/resume"),
    },
    {
      id: "theme",
      name: "Run Diagnostics matrix theme (Easter Egg)",
      category: "Utility",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      shortcut: "T H",
      action: () => {
        setIsOpen(false);
        setIsMatrixActive(true);
      },
    },
  ];

  const filteredCommands = commands.filter((cmd) =>
    cmd.name.toLowerCase().includes(search.toLowerCase()) ||
    cmd.category.toLowerCase().includes(search.toLowerCase())
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % filteredCommands.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + filteredCommands.length) % filteredCommands.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredCommands[activeIndex]) {
        filteredCommands[activeIndex].action();
      }
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[90] flex items-start justify-center pt-[15vh]">
            {/* Overlay backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-[#020205]/80 backdrop-blur-md"
            />

            {/* Dialog content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: -10 }}
              transition={{ duration: 0.2 }}
              ref={containerRef}
              className="relative w-full max-w-xl rounded-2xl border border-white/10 bg-[#0d0a1b]/95 shadow-[0_0_50px_rgba(99,102,241,0.2)] overflow-hidden z-10 p-4"
            >
              {/* Input bar */}
              <div className="flex items-center gap-3 border-b border-white/10 pb-3 mb-3">
                <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={inputRef}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setActiveIndex(0);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a command or search sections..."
                  className="w-full text-white placeholder-gray-500 bg-transparent outline-none text-sm font-medium"
                />
                <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded font-mono text-gray-400">ESC</span>
              </div>

              {/* List of items */}
              <div data-lenis-prevent className="max-h-[300px] overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                {filteredCommands.length > 0 ? (
                  filteredCommands.map((cmd, index) => {
                    const isActive = index === activeIndex;
                    return (
                      <button
                        key={cmd.id}
                        onClick={cmd.action}
                        onMouseEnter={() => setActiveIndex(index)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all text-left text-xs ${
                          isActive
                            ? "bg-indigo-500/20 text-white border-l-4 border-indigo-400 pl-2"
                            : "text-gray-400 hover:text-white"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-1.5 rounded-lg ${isActive ? "bg-indigo-500/35 text-indigo-200" : "bg-white/5 text-gray-500"}`}>
                            {cmd.icon}
                          </div>
                          <div>
                            <span className="font-semibold">{cmd.name}</span>
                            <span className="text-[9px] text-gray-500 uppercase tracking-widest block font-mono">
                              {cmd.category}
                            </span>
                          </div>
                        </div>
                        {cmd.shortcut && (
                          <span className="text-[9px] opacity-40 font-mono tracking-widest hidden sm:inline">{cmd.shortcut}</span>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="text-center py-6 text-gray-500 text-xs font-mono">
                    No matching command pipelines found.
                  </div>
                )}
              </div>

              <div className="border-t border-white/5 mt-4 pt-3 flex items-center justify-between text-[9px] text-gray-500 font-mono">
                <div className="flex gap-4">
                  <span>↑↓ Navigation</span>
                  <span>↵ Choose</span>
                </div>
                <div>Press / anywhere to toggle</div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isMatrixActive && (
          <MatrixCanvas onClose={() => setIsMatrixActive(false)} />
        )}
      </AnimatePresence>
    </>
  );
}
